import { useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import {
  Alert,
  Button,
  Group,
  Loader,
  NumberInput,
  Paper,
  ScrollArea,
  Select,
  Stack,
  Table,
  Text,
  TextInput,
  Textarea,
} from '@mantine/core';
import { notifications } from '@mantine/notifications';
import {
  IconAlertCircle,
  IconCheck,
  IconClipboardCheck,
  IconSearch,
} from '@tabler/icons-react';
import { isAxiosError } from 'axios';
import { useMutation } from '@tanstack/react-query';
import { apiClient } from '../../api/client';
import { PageHeader } from '../../components/PageHeader';
import { EmptyState } from '../../components/EmptyState';
import { useBranches } from '../branches/branchesApi';
import { useStocks } from './inventoryApi';
import type { ItemStockDto } from './types';

interface StockCountVarianceDto {
  itemId: string;
  itemNameAr: string;
  itemSku: string;
  expectedOnHand: number;
  countedQuantity: number;
  variance: number;
}

interface StockCountResponse {
  linesAccepted: number;
  variancesApplied: number;
  variances: StockCountVarianceDto[];
}

// Phase 5-Counts. Operator picks a branch, the page loads the current
// stock list, types the counted quantity per row, submits — server
// computes variances and emits Recount movements. Last submission's
// variance summary stays on screen so the operator can paste it into
// whatever inventory binder the supermarket uses.

export function StockCountPage() {
  const { t, i18n } = useTranslation();
  const branchesQuery = useBranches();
  const branches = branchesQuery.data ?? [];
  const [branchId, setBranchId] = useState<string | null>(null);
  const effectiveBranchId = branchId ?? branches[0]?.branchId ?? null;
  const stocksQuery = useStocks(effectiveBranchId);

  const [search, setSearch] = useState('');
  const [counted, setCounted] = useState<Record<string, number | ''>>({});
  const [notes, setNotes] = useState('');
  const [result, setResult] = useState<StockCountResponse | null>(null);

  const submitMutation = useMutation({
    mutationFn: async () => {
      if (!effectiveBranchId) throw new Error('No branch');
      const lines = Object.entries(counted)
        .filter(([, v]) => typeof v === 'number')
        .map(([itemId, v]) => ({ itemId, countedQuantity: v as number }));
      const resp = await apiClient.post<StockCountResponse>(
        '/api/inventory/stock-counts',
        {
          branchId: effectiveBranchId,
          notes: notes.trim() || null,
          lines,
        },
      );
      return resp.data;
    },
    onSuccess: (data) => {
      setResult(data);
      setCounted({});
      notifications.show({
        color: 'green',
        title: t('stockCount.successTitle'),
        message: t('stockCount.successMessage', {
          lines: data.linesAccepted,
          applied: data.variancesApplied,
        }),
        icon: <IconCheck size={16} stroke={1.8} />,
      });
      stocksQuery.refetch();
    },
    onError: (err) => {
      const msg = isAxiosError(err)
        ? ((err.response?.data as { detail?: string } | undefined)?.detail ?? err.message)
        : t('common.toasts.errorBody');
      notifications.show({
        color: 'red',
        title: t('stockCount.failTitle'),
        message: msg,
      });
    },
  });

  const branchOptions = useMemo(
    () =>
      branches.map((b) => ({
        value: b.branchId,
        label: i18n.language === 'ar' ? b.nameAr : (b.nameEn ?? b.nameAr),
      })),
    [branches, i18n.language],
  );

  const filtered = useMemo(() => {
    const stocks = stocksQuery.data ?? [];
    const q = search.trim().toLowerCase();
    if (!q) return stocks;
    return stocks.filter(
      (s) =>
        s.itemNameAr.toLowerCase().includes(q) ||
        s.itemNameEn?.toLowerCase().includes(q) ||
        s.itemSku.toLowerCase().includes(q),
    );
  }, [stocksQuery.data, search]);

  const enteredCount = Object.values(counted).filter(
    (v) => typeof v === 'number',
  ).length;

  return (
    <Stack gap="lg">
      <PageHeader
        eyebrow={t('nav.sections.operations')}
        title={t('stockCount.page.title')}
        subtitle={t('stockCount.page.subtitle')}
        actions={
          <Button
            onClick={() => submitMutation.mutate()}
            loading={submitMutation.isPending}
            disabled={!effectiveBranchId || enteredCount === 0}
            leftSection={<IconClipboardCheck size={16} stroke={1.8} />}
          >
            {t('stockCount.page.submit', { count: enteredCount })}
          </Button>
        }
      />

      <Paper p="md">
        <Group gap="md" wrap="nowrap" align="flex-end">
          <Select
            label={t('stockCount.page.branchLabel')}
            data={branchOptions}
            value={effectiveBranchId}
            onChange={(v) => {
              setBranchId(v);
              setCounted({});
              setResult(null);
            }}
            searchable
            disabled={branches.length === 0 || branchesQuery.isLoading}
            maw={300}
          />
          <TextInput
            label={t('stockCount.page.searchLabel')}
            placeholder={t('stockCount.page.searchPlaceholder')}
            leftSection={<IconSearch size={16} stroke={1.6} />}
            value={search}
            onChange={(e) => setSearch(e.currentTarget.value)}
            style={{ flex: 1 }}
          />
          <Textarea
            label={t('stockCount.page.notesLabel')}
            placeholder={t('stockCount.page.notesPlaceholder')}
            value={notes}
            onChange={(e) => setNotes(e.currentTarget.value)}
            minRows={1}
            maxRows={2}
            style={{ flex: 1 }}
          />
        </Group>
      </Paper>

      {result && (
        <Alert
          color={result.variancesApplied === 0 ? 'green' : 'orange'}
          icon={
            result.variancesApplied === 0 ? (
              <IconCheck size={16} stroke={1.8} />
            ) : (
              <IconAlertCircle size={16} stroke={1.8} />
            )
          }
          variant="light"
          title={t('stockCount.result.title', {
            lines: result.linesAccepted,
            applied: result.variancesApplied,
          })}
        >
          {result.variances.length === 0 ? null : (
            <Table verticalSpacing="xs" fz="sm" mt="xs">
              <Table.Thead>
                <Table.Tr>
                  <Table.Th>{t('stockCount.result.columns.item')}</Table.Th>
                  <Table.Th className="text-end">
                    {t('stockCount.result.columns.expected')}
                  </Table.Th>
                  <Table.Th className="text-end">
                    {t('stockCount.result.columns.counted')}
                  </Table.Th>
                  <Table.Th className="text-end">
                    {t('stockCount.result.columns.variance')}
                  </Table.Th>
                </Table.Tr>
              </Table.Thead>
              <Table.Tbody>
                {result.variances.map((v) => (
                  <Table.Tr key={v.itemId}>
                    <Table.Td>
                      <Text size="sm" fw={500}>
                        {v.itemNameAr}
                      </Text>
                      <Text size="xs" c="dimmed" className="font-mono">
                        {v.itemSku}
                      </Text>
                    </Table.Td>
                    <Table.Td className="font-numeric text-end">
                      {v.expectedOnHand}
                    </Table.Td>
                    <Table.Td className="font-numeric text-end">
                      {v.countedQuantity}
                    </Table.Td>
                    <Table.Td className="font-numeric text-end">
                      <Text
                        size="sm"
                        fw={600}
                        c={
                          v.variance === 0
                            ? 'var(--wheels-ink-muted)'
                            : v.variance > 0
                              ? 'green'
                              : 'red'
                        }
                      >
                        {v.variance > 0 ? '+' : ''}
                        {v.variance}
                      </Text>
                    </Table.Td>
                  </Table.Tr>
                ))}
              </Table.Tbody>
            </Table>
          )}
        </Alert>
      )}

      <Paper p={filtered.length === 0 ? 'lg' : 0}>
        {!effectiveBranchId ? (
          <EmptyState
            icon={<IconClipboardCheck size={22} stroke={1.6} />}
            title={t('stockCount.empty.noBranchesTitle')}
            description={t('stockCount.empty.noBranchesBody')}
          />
        ) : stocksQuery.isLoading ? (
          <Stack align="center" py={48}>
            <Loader size="sm" />
          </Stack>
        ) : filtered.length === 0 ? (
          <EmptyState
            icon={<IconClipboardCheck size={22} stroke={1.6} />}
            title={t('stockCount.empty.noStockTitle')}
            description={t('stockCount.empty.noStockBody')}
          />
        ) : (
          <ScrollArea>
            <Table verticalSpacing="sm" highlightOnHover striped="even">
              <Table.Thead>
                <Table.Tr>
                  <Table.Th>{t('stockCount.columns.item')}</Table.Th>
                  <Table.Th className="text-end">
                    {t('stockCount.columns.onHand')}
                  </Table.Th>
                  <Table.Th style={{ width: 140 }}>
                    {t('stockCount.columns.counted')}
                  </Table.Th>
                  <Table.Th className="text-end" style={{ width: 100 }}>
                    {t('stockCount.columns.variance')}
                  </Table.Th>
                </Table.Tr>
              </Table.Thead>
              <Table.Tbody>
                {filtered.map((s) => renderRow(s, counted, setCounted))}
              </Table.Tbody>
            </Table>
          </ScrollArea>
        )}
      </Paper>
    </Stack>
  );
}

function renderRow(
  s: ItemStockDto,
  counted: Record<string, number | ''>,
  setCounted: (v: Record<string, number | ''>) => void,
) {
  const value = counted[s.itemId];
  const variance = typeof value === 'number' ? value - s.onHand : null;
  return (
    <Table.Tr key={s.itemId}>
      <Table.Td>
        <Text size="sm" fw={500}>
          {s.itemNameAr}
        </Text>
        <Text size="xs" c="dimmed" className="font-mono">
          {s.itemSku}
        </Text>
      </Table.Td>
      <Table.Td className="font-numeric text-end">{s.onHand}</Table.Td>
      <Table.Td>
        <NumberInput
          value={value ?? ''}
          onChange={(v) =>
            setCounted({
              ...counted,
              [s.itemId]: typeof v === 'string' ? Number(v) : v,
            })
          }
          min={0}
          decimalScale={3}
          size="xs"
          placeholder="—"
        />
      </Table.Td>
      <Table.Td className="font-numeric text-end">
        {variance === null ? (
          <Text size="sm" c="dimmed">
            —
          </Text>
        ) : (
          <Text
            size="sm"
            fw={600}
            c={variance === 0 ? 'var(--wheels-ink-muted)' : variance > 0 ? 'green' : 'red'}
          >
            {variance > 0 ? '+' : ''}
            {variance}
          </Text>
        )}
      </Table.Td>
    </Table.Tr>
  );
}
